/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

function l_getFocusableElements(container) {
	return Array.from(
		container.querySelectorAll(
			"summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
		)
	).filter((element) => !element.closest('[aria-hidden="true"]'))
}

function l_disableFocusableElements(container) {
	const focusableElements = l_getFocusableElements(container)
	focusableElements.forEach((element) => {
		element.setAttribute('tabindex', '-1')
		element.setAttribute('aria-disabled', 'true')
	})
}

function l_enableFocusableElements(container) {
	const focusableElements = l_getFocusableElements(container)

	focusableElements.forEach((element) => {
		element.removeAttribute('tabindex')
		element.removeAttribute('aria-disabled')
	})
}

function formatMoney(cents, format = '') {
	let value = ''
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
	const formatString = format || window.theme.settings.moneyFormat || '${{amount}}'

	if (typeof cents == 'string') {
		cents = cents.replace('.', '')
	}

	function defaultOption(opt, def) {
		return typeof opt == 'undefined' ? def : opt
	}

	function formatWithDelimiters(number, precision, thousands, decimal) {
		precision = defaultOption(precision, 2)
		thousands = defaultOption(thousands, ',')
		decimal = defaultOption(decimal, '.')

		if (isNaN(number) || number == null) {
			return 0
		}

		number = (number / 100.0).toFixed(precision)

		var parts = number.split('.'),
			dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
			cents = parts[1] ? decimal + parts[1] : ''

		return dollars + cents
	}

	switch (formatString.match(placeholderRegex)[1]) {
		case 'amount':
			value = formatWithDelimiters(cents, 2)
			break
		case 'amount_no_decimals':
			value = formatWithDelimiters(cents, 0, ' ', '.')
			break
		case 'amount_with_comma_separator':
			value = formatWithDelimiters(cents, 2, '.', '.')
			break
		case 'amount_no_decimals_with_comma_separator':
			value = formatWithDelimiters(cents, 0, '.', '.')
			break
	}

	return formatString.replace(placeholderRegex, value)
}

function l_fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	}
}

const L_ATC = {
	cartDrawer: null,
	handleConfigWithFormEl(formEl) {
		const config = l_fetchConfig('javascript')
		config.headers['X-Requested-With'] = 'XMLHttpRequest'
		delete config.headers['Content-Type']

		const formData = new FormData(formEl)

		config.body = formData

		return config
	},
	handleConfigWithVariantId(addingData) {
		// Setup config object
		const config = l_fetchConfig('javascript')
		config.headers['X-Requested-With'] = 'XMLHttpRequest'
		delete config.headers['Content-Type']

		// Setup form data
		const formData = new FormData()

		addingData.forEach((item, index0) => {
			formData.append(`items[${index0}][quantity]`, item.quantity)
			formData.append(`items[${index0}][id]`, item.id)
		})

		// Insert sections
		const sectionsToUpdate = ['page-header', 'cart-drawer'].map((sel) => document.querySelector(sel)).filter((el) => el)
		const sectionIds = sectionsToUpdate.map((el) => el.dataset.sectionId)
		formData.append('sections_url', window.location.pathname)
		formData.append('sections', sectionIds.join(','))

		// Insert formData into config
		config.body = formData

		return config
	},
	addToCart(config, id = '234na08fa3') {
		return fetch(window.theme.routes.cartAdd, config)
			.then((response) => response.json())
			.then((response) => {
				console.log(response)

				// ATC: Handle error case
				if (response.status) {
					document.dispatchEvent(new CustomEvent('l:cart:error', { detail: { id, description: response.description } }))

					return
				}

				// Handle update content
				if (document.querySelector('.template-cart')) {
					// Cart page
					const cartForm = document.querySelector('cart-form')
					if (cartForm && cartForm.enableAjaxUpdate) {
						cartForm.refresh()
					}
				} else if (theme.settings.afterAddToCart === 'page') {
					// Allow the tick animation to complete
					setTimeout(() => {
						window.location.href = theme.routes.cart
					}, 300)
				} else {
					const sectionsToUpdate = ['page-header', 'cart-drawer']
						.map((sel) => document.querySelector(sel))
						.filter((el) => el)

					sectionsToUpdate.forEach((el) => {
						el.updateFromCartChange(response.sections[el.dataset.sectionId])
					})

					if (theme.settings.afterAddToCart === 'notification') {
						const notification = document.getElementById('AddedNotification').content.firstElementChild.cloneNode(true)
						notification.dataset.productTitle = response.product_title
						let notificationContainer = document.querySelector('.pageheader--sticky')
						if (!notificationContainer) {
							notificationContainer = document.querySelector('body')
						}
						notificationContainer.appendChild(notification)
					} else if (theme.settings.afterAddToCart === 'drawer') {
						document.querySelector('.js-cart-drawer').open()
					}
				}

				this.dispatchEvent(
					new CustomEvent('on:cart:add', {
						bubbles: true,
						detail: {
							variantId: response.variant_id,
						},
					})
				)

				// Publish added to cart event
				document.dispatchEvent(new CustomEvent('l:cart:updated', { bubbles: true, detail: { id, response } }))
			})
	},
}

/************************************************************************************

COMPONENT: SWIPER CAROUSEL

*************************************************************************************/
if (!customElements.get('l-collapsible-tab')) {
	customElements.define(
		'l-collapsible-tab',
		class LTACollapsibleTab extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.trigger = this.querySelector('[data-trigger]')
				this.content = this.querySelector('[data-content]')

				// Data
				this.expandedClass = 'is-expanded'
				this.collapsedClass = 'is-collapsed'
				this.collapsedHeight = '0px'
				this.oneAtATime = true

				this.transitionendEvent = this.transitionendEventName()

				// Setup default behavior / data
				this.dataset.oneOpen === 'false' && (this.oneAtATime = false)

				if (this.getAttribute('open') === 'true') {
					this.selected = true
					this.classList.add(this.expandedClass)
					this.setExpandedAria()
				} else {
					this.content.style.height = this.collapsedHeight
					this.classList.add(this.collapsedClass)
					this.setCollapsedAria()
					l_disableFocusableElements(this.content)
				}

				this.content.removeAttribute('hidden')

				// Events
				this.trigger.addEventListener('click', (e) => this.toggle(e))
			}

			toggle(e) {
				e.preventDefault()

				this.selected ? this.close() : this.open()

				/* Close another tab and open only the current tab. */
				if (this.selected && this.oneAtATime) {
					const tabsContainer = this.closest('[data-collapsible-wrapper]')
					const collapsibleTabs = tabsContainer?.querySelectorAll('l-collapsible-tab')

					collapsibleTabs?.length &&
						collapsibleTabs.forEach((tab) => {
							tab !== this && tab.selected && tabsContainer !== tab && tab.close()
						})
				}
			}

			open() {
				this.selected = true
				this.expand()
				this.setAttribute('open', true)
				l_enableFocusableElements(this.content)
			}

			close() {
				this.selected = false
				this.collapse()
				this.removeAttribute('open')
				l_disableFocusableElements(this.content)
			}

			expand() {
				const onTransitionEnd = ((event) => {
					if (event.target === this.content) {
						this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

						this.isOpen &&
							requestAnimationFrame(() => {
								this.content.style.transition = '0'
								this.content.style.height = 'auto'

								requestAnimationFrame(() => {
									this.content.style.height = null
									this.content.style.transition = null
									this.setExpandedAria()
								})
							})
					}
				}).bind(this)

				this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
				this.isOpen = true
				this.classList.add(this.expandedClass)
				this.classList.remove(this.collapsedClass)
				this.content.style.height = this.content.scrollHeight + 'px'
			}

			collapse() {
				const onTransitionEnd = ((event) => {
					if (event.target === this.content) {
						this.content.removeEventListener(this.transitionendEvent, onTransitionEnd)

						if (!this.isOpen) {
							this.setCollapsedAria()
							this.classList.add(this.collapsedClass)
						}
					}
				}).bind(this)

				this.content.addEventListener(this.transitionendEvent, onTransitionEnd)
				this.isOpen = false
				this.classList.remove(this.expandedClass)

				requestAnimationFrame(() => {
					this.content.style.transition = '0'
					this.content.style.height = this.content.scrollHeight + 'px'

					requestAnimationFrame(() => {
						this.content.style.transition = null
						this.content.style.height = this.collapsedHeight
					})
				})
			}

			destroy() {
				this.trigger && this.trigger.removeEventListener('click', (e) => this.toggle(e))
				this.content && this.content.removeAttribute('aria-hidden')
				this.content && (this.content.style.height = 'auto')

				this.classList.remove(this.expandedClass, this.collapsedClass)
				this.removeAttribute('open')
			}

			transitionendEventName() {
				let eventName,
					testElement = document.createElement('div'),
					eventNames = {
						transition: 'transitionend',
						OTransition: 'otransitionend',
						MozTransition: 'transitionend',
						WebkitTransition: 'webkitTransitionEnd',
					}

				for (eventName in eventNames) {
					if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
						return eventNames[eventName]
					}
				}
			}

			setExpandedAria() {
				this.trigger.setAttribute('aria-expanded', 'true')
				this.content.removeAttribute('aria-hidden')
				this.content.removeAttribute('tabindex')
			}

			setCollapsedAria() {
				this.trigger.setAttribute('aria-expanded', 'false')
				this.content.setAttribute('aria-hidden', 'true')
				this.content.setAttribute('tabindex', '-1')
			}
		}
	)
}

if (!customElements.get('l-swiper-carousel')) {
	customElements.define(
		'l-swiper-carousel',
		class LTASwiperCarousel extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Check if Swiper is already loaded
				if (window.Swiper) {
					this.setupCarousel()

					return
				}

				document.addEventListener('carousel:loaded', () => {
					this.setupCarousel()
				})
			}

			setupCarousel() {
				this.carouselConfig = JSON.parse(this.querySelector('[data-carousel-config]').textContent)

				if (!this.carouselConfig) return

				this.classList.remove('is-placeholder')
				this.querySelectorAll('.swiper-slide').forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

				this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
			}

			slideTo(index = 0) {
				this.swiperCarousel.slideTo(index)
			}
		}
	)
}

/************************************************************************************

* PRODUCT PAGE

*************************************************************************************/

/***************************************
 * [COMPONENT]: STICKY ADD TO CART
 ****************************************/
if (!customElements.get('l-product-sticky-atc')) {
	customElements.define(
		'l-product-sticky-atc',
		class LTAProductStickyATC extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Data
				this.sectionId = this.dataset.sectionId

				// Elements
				this.submitBtnEl = this.querySelector('[data-atc]')
				this.qtyInputEl = this.querySelector('[data-qty]')
				this.priceEl = this.querySelector('[data-price]')
				this.realSubmitBtnEl = document.getElementById(`mainProductBuyButtons`).querySelector('[type="submit"]')
				this.realQtyInputEl = document.getElementById(`mainProductBuyButtons`).querySelector('[name="quantity"]')

				// Click event
				this.submitBtnEl.addEventListener('click', () => {
					this.submitBtnEl.classList.add('is-loading')

					debounce(() => {
						this.submitBtnEl.classList.remove('is-loading')
					}, 3000)()
				})

				// Cart update event
				document.addEventListener('on:cart:add', (event) => {
					this.submitBtnEl.classList.remove('is-loading')
				})

				document.addEventListener('on:variant:change', this.onVariantChange.bind(this))

				this.handleScrollEvent()
				this.setupOnQtyChangeEvent()
			}

			disconnectedCallback() {
				this.productAddedToCart()
			}

			handleScrollEvent() {
				const handleScrollEvent = () => {
					const rect = this.realSubmitBtnEl.getBoundingClientRect()
					const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight

					this.classList.toggle('is-active', !isVisible)
				}

				window.addEventListener('scroll', debounce(handleScrollEvent, 100))
				handleScrollEvent()
			}

			onVariantChange(event) {
				const { variant } = event.detail

				this.priceEl.innerHTML = formatMoney(variant.price)
				this.submitBtnEl.classList.toggle('is-atc', variant.available)
			}

			setupOnQtyChangeEvent() {
				this.addEventListener('change', (event) => {
					const currentQty = event.target.value

					this.realQtyInputEl.value = currentQty
					this.realQtyInputEl.dispatchEvent(new Event('change', { bubbles: true }))
				})

				this.realQtyInputEl.addEventListener('change', (event) => {
					const currentQty = event.target.value

					this.qtyInputEl.value = currentQty
					this.qtyInputEl.dispatchEvent(new Event('change', { bubbles: true }))
				})
			}
		}
	)
}

/***************************************
 * [COMPONENT]: RECOMMENDATION PRODUCTS
 ****************************************/
if (!customElements.get('l-product-recommendations')) {
	customElements.define(
		'l-product-recommendations',
		class LTAProductRecommendations extends HTMLElement {
			observer = undefined

			constructor() {
				super()
			}

			connectedCallback() {
				this.initializeRecommendations(this.dataset.productId)
			}

			initializeRecommendations(productId) {
				this.observer?.unobserve(this)
				this.observer = new IntersectionObserver(
					(entries, observer) => {
						if (!entries[0].isIntersecting) return
						observer.unobserve(this)
						this.loadRecommendations(productId)
					},
					{ rootMargin: '0px 0px 400px 0px' }
				)
				this.observer.observe(this)
			}

			loadRecommendations(productId) {
				console.log(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)

				fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
					.then((response) => response.text())
					.then((text) => {
						const html = document.createElement('div')
						html.innerHTML = text
						const recommendations = html.querySelector('l-product-recommendations')
						const isExistProducts = this.querySelector('l-swiper-carousel')

						if (recommendations?.innerHTML.trim().length) {
							this.innerHTML = recommendations.innerHTML
						}

						if (!isExistProducts) this.remove()
					})
					.catch((e) => {
						console.error(e)
					})
			}
		}
	)
}

/***************************************
 * [COMPONENT]: UP-SELL PRODUCTS
 ****************************************/
if (!customElements.get('l-product-upsells')) {
	customElements.define(
		'l-product-upsells',
		class LTAProductUpsells extends HTMLElement {
			observer = undefined

			constructor() {
				super()
			}

			connectedCallback() {}
		}
	)
}

/***************************************
 * [COMPONENT]: UP-SELL PRODUCTS
 ****************************************/
if (!customElements.get('l-product-card')) {
	customElements.define(
		'l-product-card',
		class LTAProductCard extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.imageEls = this.querySelectorAll('[data-variant-image]')
				this.defaultImageEl = this.querySelector('[data-default-image]')
				this.linkEls = this.querySelectorAll('[data-link]')
				this.atcButtonEl = this.querySelector('[data-atc]')
				this.priceEl = this.querySelector('[data-price]')
				this.reductionPriceEl = this.querySelector('[data-reduction-price]')

				// Data
				this.productData = JSON.parse(this.querySelector('script[type="application/json"]').textContent)
				this.productURL = this.dataset.productUrl
				this.selectedVariantId = this.dataset.selectedVariantId

				// Events
				this.addEventListener('input', this.handleOnVariantChange.bind(this))
				this.atcButtonEl.addEventListener('click', this.handleOnAddToCart.bind(this))
			}

			handleOnVariantChange(event) {
				const checkInputEl = this.querySelector('input:checked')
				const selectedVariantId = checkInputEl.value

				// Update image
				const hasMatchedImage = Array.from(this.imageEls).some((imageEl) => {
					const isActive = imageEl.dataset.variantImage == selectedVariantId
					imageEl.classList.toggle('is-active', isActive)
					return isActive
				})

				if (!hasMatchedImage) this.defaultImageEl.classList.add('is-active')

				// Update links
				const newProductURL = `${this.productURL}${this.productURL.includes('?') ? '&' : '?'}variant=${selectedVariantId}`
				this.linkEls.forEach((linkEl) => linkEl.setAttribute('href', newProductURL))

				// Get selected variant
				const selectedVariant = this.productData.variants.find((variant) => variant.id == selectedVariantId)
				this.selectedVariantId = selectedVariant.id

				// Update prices
				if (this.priceEl) {
					const { price, compare_at_price } = selectedVariant
					const reductionPrice = compare_at_price > price ? formatMoney(compare_at_price - price) : null

					this.priceEl.innerHTML = formatMoney(price)
					this.reductionPriceEl.innerHTML = reductionPrice
						? window.theme.strings.productCard.reductionPrice.replace('{{ amount }}', reductionPrice)
						: ''
				}

				// Update ATC button
				this.atcButtonEl.toggleAttribute('disabled', !selectedVariant.available)
				this.atcButtonEl.innerHTML = `${window.theme.strings.productCard.addToCart} ${formatMoney(selectedVariant.price)}`
			}

			handleOnAddToCart() {
				this.atcButtonEl.classList.add('is-loading')

				const config = L_ATC.handleConfigWithVariantId([
					{
						id: this.selectedVariantId,
						quantity: 1,
					},
				])

				L_ATC.addToCart(config, 'product-card').finally(() => {
					this.atcButtonEl.classList.remove('is-loading')
				})
			}
		}
	)
}

/***************************************
 * [COMPONENT]: UP-SELL PRODUCTS
 ****************************************/
if (!customElements.get('l-header-cart')) {
	customElements.define(
		'l-header-cart',
		class LTAHeaderCart extends HTMLElement {
			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.cartCartEl = this.querySelector('.cart-link__count')
				this.cartLinkEl = this.querySelector('.cart-link')

				theme.addDelegateEventListener(this, 'click', '.cart-link', (evt) => {
					evt.preventDefault()
					document.querySelector('.js-cart-drawer').open()
				})

				// Update cart count
				this.refreshHeader = () => {
					fetch(`${window.location.origin}?sections=header`)
						.then((response) => response.json())
						.then((data) => {
							const template = document.createElement('template')
							template.innerHTML = data.header

							const newEl = template.content.querySelector('.cart-link')

							this.cartLinkEl.innerHTML = newEl.innerHTML
						})
				}

				document.addEventListener('on:cart:add', this.refreshHeader)
				document.addEventListener('on:cart:change', this.refreshHeader)
			}
		}
	)
}

